import type { PurchaseTrendCell } from '@erp/rpc';
import { serializeMoney, sumMoney, toMoneyNumber } from '@erp/shared-utils';
import type { BreakdownCell } from '@/components/common/breakdown-tables/types';
import { MONTH_ORDER } from '@/utils/breakdown-transforms/monthOrdering';

export type PurchaseTrendTableView = 'divisions' | 'suppliers';

export interface PurchaseTrendTableLabels {
  purchases: string;
  rebates: string;
  ytd: string;
  notInGently: string;
}

function purchaseTrendMonthColumn(month: number): string {
  return MONTH_ORDER[month - 1] ?? '';
}

export function exactYtdFromAmounts(
  amounts: ReadonlyArray<number | null>,
): string | null {
  const present: string[] = [];
  for (const amount of amounts) {
    if (amount !== null) {
      present.push(serializeMoney(amount));
    }
  }
  if (present.length === 0) {
    return null;
  }
  return serializeMoney(sumMoney(present));
}

export function purchaseTrendEntityLabel(
  name: string,
  notInGently: boolean,
  phrase: string,
  code?: string | null,
): string {
  if (!notInGently) {
    return name;
  }
  const detail = code || name;
  return detail.length > 0 ? `${phrase} · ${detail}` : phrase;
}

export function purchaseTrendColumnOrder(ytd: string): string[] {
  return [...MONTH_ORDER, ytd];
}

export function purchaseTrendSubColumnOrder(
  purchases: string,
  rebates: string,
): string[] {
  return [purchases, rebates];
}

export function aggregatePurchaseTrendValues(
  values: BreakdownCell['value'][],
): BreakdownCell['value'] {
  const amounts: string[] = [];
  for (const value of values) {
    if (typeof value === 'number' && !Number.isNaN(value)) {
      amounts.push(serializeMoney(value));
    }
  }
  if (amounts.length === 0) {
    return null;
  }
  return toMoneyNumber(serializeMoney(sumMoney(amounts)));
}

export function leafIdentity(cell: PurchaseTrendCell): string {
  const supplier =
    cell.supplierId ??
    `code:${(cell.supplierCode ?? cell.supplierName).toUpperCase()}`;
  const division = cell.divisionId ?? `name:${cell.divisionName.toUpperCase()}`;
  return `${supplier}|${division}`;
}

export function supplierDisplayLabel(
  cell: PurchaseTrendCell,
  phrase: string,
): string {
  return purchaseTrendEntityLabel(
    cell.supplierName,
    cell.notInGentlySupplier,
    phrase,
    cell.supplierCode,
  );
}

export function divisionDisplayLabel(
  cell: PurchaseTrendCell,
  phrase: string,
): string {
  return purchaseTrendEntityLabel(
    cell.divisionName,
    cell.notInGentlyDivision,
    phrase,
  );
}

function groupCellsByLeaf(
  cells: PurchaseTrendCell[],
): Map<string, PurchaseTrendCell[]> {
  const groups = new Map<string, PurchaseTrendCell[]>();
  for (const cell of cells) {
    const key = leafIdentity(cell);
    const existing = groups.get(key);
    if (existing) {
      existing.push(cell);
    } else {
      groups.set(key, [cell]);
    }
  }
  return groups;
}

function moneyValue(amount: number): number {
  return toMoneyNumber(serializeMoney(amount));
}

function pushMetricCell(
  data: BreakdownCell[],
  column: string,
  subColumn: string,
  row: string,
  subRow: string,
  subRowKey: string,
  amount: number | null,
): void {
  if (amount === null) {
    return;
  }
  data.push({
    column,
    subColumn,
    row,
    subRow,
    subRowKey,
    value: moneyValue(amount),
  });
}

function pushYtdCell(
  data: BreakdownCell[],
  ytdColumn: string,
  subColumn: string,
  row: string,
  subRow: string,
  subRowKey: string,
  ytd: string | null,
): void {
  if (ytd === null) {
    return;
  }
  data.push({
    column: ytdColumn,
    subColumn,
    row,
    subRow,
    subRowKey,
    value: toMoneyNumber(ytd),
  });
}

function appendLeafBreakdownCells(
  data: BreakdownCell[],
  group: PurchaseTrendCell[],
  view: PurchaseTrendTableView,
  labels: PurchaseTrendTableLabels,
): void {
  const first = group[0];
  if (!first) {
    return;
  }
  const division = divisionDisplayLabel(first, labels.notInGently);
  const supplier = supplierDisplayLabel(first, labels.notInGently);
  const row = view === 'divisions' ? division : supplier;
  const subRow = view === 'divisions' ? supplier : division;
  const subRowKey = leafIdentity(first);
  for (const cell of group) {
    const column = purchaseTrendMonthColumn(cell.month);
    if (column.length === 0) {
      continue;
    }
    pushMetricCell(
      data,
      column,
      labels.purchases,
      row,
      subRow,
      subRowKey,
      cell.purchases,
    );
    pushMetricCell(
      data,
      column,
      labels.rebates,
      row,
      subRow,
      subRowKey,
      cell.reportedRebates,
    );
  }
  pushYtdCell(
    data,
    labels.ytd,
    labels.purchases,
    row,
    subRow,
    subRowKey,
    exactYtdFromAmounts(group.map((cell) => cell.purchases)),
  );
  pushYtdCell(
    data,
    labels.ytd,
    labels.rebates,
    row,
    subRow,
    subRowKey,
    exactYtdFromAmounts(group.map((cell) => cell.reportedRebates)),
  );
}

export function mapPurchaseTrendCellsToBreakdown(
  cells: PurchaseTrendCell[],
  view: PurchaseTrendTableView,
  labels: PurchaseTrendTableLabels,
): BreakdownCell[] {
  const data: BreakdownCell[] = [];
  for (const group of groupCellsByLeaf(cells).values()) {
    appendLeafBreakdownCells(data, group, view, labels);
  }
  return data;
}

export function groupedPurchaseTrendLeaves(
  cells: PurchaseTrendCell[],
): PurchaseTrendCell[][] {
  return Array.from(groupCellsByLeaf(cells).values());
}
